import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../../shared/types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
export function PropertyInformation() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data for a Malaysian island resort
  const [property, setProperty] = useState<Property>({
    name: 'Baa Atoll Lagoon Paradise Resort',
    description: 'Nestled on the pristine shores of Langkawi, Langkawi Lagoon Paradise Resort offers a perfect blend of Malaysian luxury and natural beauty. Surrounded by turquoise lagoons and overlooking the crystal-clear waters of the Andaman Sea, this exclusive retreat provides an authentic Malaysian experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Overwater Villas', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Malaysian Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: 'Eydhafushi Island',
      city: 'Baa Atoll',
      postalCode: '06010',
      country: 'Malaysia',
      latitude: '5.1042',
      longitude: '73.0680'
    },
    contact: {
      email: 'reservations@baaatolllagoon.mv',
      phone: '+960 660 1234',
      website: 'https://www.baaatolllagoonresort.mv',
      timezone: 'Asia/Kuala_Lumpur'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Malaysian Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Malaysian Massage', 'Bodu Beru Performances', 'Dolphin Cruise Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-9344">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-9345">
            {tag}
            <button aria-label={`Remove ${tag}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-9346">
              <XIcon size={12} aria-hidden="true" data-id="element-9347" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-9348">
      {/* Simplified header section */}
      <div data-id="element-9349">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-9350">
          {t('pages.PropertyInformation.hotel')}
        </h1>
        <div className="flex justify-between items-center" data-id="element-9351">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-9352">
            <div className="flex space-x-6" data-id="element-9353">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-9354">
                {t('pages.PropertyInformation.hotel_information')}
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-9355">
                {t('components.EditRoomForm.media_assets')}
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-9356">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-9357">
              <span className="mr-2" data-id="element-9358">
                {t('pages.PropertyInformation.draft_submitted_for_review')}
              </span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-9359" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-9360">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-9361">
                {t('pages.PropertyInformation.draft_status')}
              </h3>
              <p className="text-xs text-gray-600" data-id="element-9362">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-9363">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9364">
          <div data-id="element-9365">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9366">
              {t('pages.PropertyInformation.hotel_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-9367" />
          </div>
          <div data-id="element-9368">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9369">
              {t('pages.PropertyInformation.designation')}
            </label>
            <div className="relative" data-id="element-9370">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-9371">
                <option value="Resort" data-id="element-9372">
                  {t('pages.PropertyInformation.resort')}
                </option>
                <option value="Hotel" data-id="element-9373">
                  {t('pages.PropertyInformation.hotel')}
                </option>
                <option value="Villa" data-id="element-9374">
                  {t('pages.PropertyInformation.villa')}
                </option>
                <option value="Guesthouse" data-id="element-9375">
                  {t('pages.PropertyInformation.guesthouse')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9376">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9377" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9378">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9379">
            {t('components.AddRoomForm.description')}
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-9380" />
        </div>
        <div className="mb-6" data-id="element-9381">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9382">
            {t('pages.PropertyInformation.star_rating')}
          </label>
          <div className="relative" data-id="element-9383">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-9384">
              <option value="5" data-id="element-9385">5</option>
              <option value="4" data-id="element-9386">4</option>
              <option value="3" data-id="element-9387">3</option>
              <option value="2" data-id="element-9388">2</option>
              <option value="1" data-id="element-9389">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9390">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9391" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9392">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9393">
            {t('pages.PropertyInformation.tags')}
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9394">
          <div data-id="element-9395">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9396">
              {t('pages.PropertyInformation.address')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-9397" />
          </div>
          <div data-id="element-9398">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9399">
              {t('pages.PropertyInformation.area_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Pantai Cenang" data-id="element-9400" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9401">
          <div data-id="element-9402">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9403">
              {t('pages.PropertyInformation.city')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-9404" />
          </div>
          <div data-id="element-9405">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9406">
              {t('pages.PropertyInformation.postal_code')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-9407" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-9408">
          <div data-id="element-9409">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9410">
              {t('pages.PropertyInformation.country')}
            </label>
            <div className="relative" data-id="element-9411">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-9412">
                <option value="Malaysia" data-id="element-9413">
                  {t('pages.PropertyInformation.malaysia')}
                </option>
                <option value="Sri Lanka" data-id="element-9414">
                  {t('pages.PropertyInformation.indonesia')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9415">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9416" />
              </div>
            </div>
          </div>
          <div data-id="element-9417">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9418">
              {t('pages.PropertyInformation.latitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-9419" />
          </div>
          <div data-id="element-9420">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9421">
              {t('pages.PropertyInformation.longitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-9422" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9423">
          <div data-id="element-9424">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9425">
              {t('pages.PropertyInformation.contact_email')}
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-9426" />
          </div>
          <div data-id="element-9427">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9428">
              {t('pages.PropertyInformation.phone_number')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-9429" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9430">
          <div data-id="element-9431">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9432">
              {t('pages.PropertyInformation.website')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-9433" />
          </div>
          <div data-id="element-9434">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9435">
              {t('pages.PropertyInformation.timezone')}
            </label>
            <div className="relative" data-id="element-9436">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-9437">
                <option value="Asia/Kuala_Lumpur" data-id="element-9438">
                  {t('pages.PropertyInformation.asiakualalumpur')}
                </option>
                <option value="Asia/Singapore" data-id="element-9439">
                  {t('pages.PropertyInformation.asiasingapore')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9440">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9441" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9442">
          <div data-id="element-9443">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9444">
              {t('pages.PropertyInformation.checkin_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-9445" />
          </div>
          <div data-id="element-9446">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9447">
              {t('pages.PropertyInformation.checkout_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-9448" />
          </div>
        </div>
        <div className="mb-6" data-id="element-9449">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9450">
            {t('pages.PropertyInformation.hotel_amenities')}
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-9451">
            <div className="flex flex-wrap gap-2" data-id="element-9452">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-9453">
                  {amenity}
                  <button aria-label={`Remove ${amenity}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-9454">
                    <XIcon size={12} aria-hidden="true" data-id="element-9455" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-9456">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9457">
            {t('pages.PropertyInformation.transportation')}
          </label>
          <div className="space-y-2" data-id="element-9458">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-9459">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-9460" />

                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-9461">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-9462">
      <polyline points="6 9 12 15 18 9" data-id="element-9463"></polyline>
    </svg>;
}